import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useDebounceFn } from 'ahooks';
import type { SupportedCountry } from 'generated/graphql';
import { FindPlaceApiProviderEnum, FindPlaceTypeEnum } from 'generated/graphql';
import {
    getPlaceDropdownOptions,
    getRegionNameByCountry,
} from '~/modules/location/util';
import type { DetailedPlace } from '~/modules/location/type';
import { FIND_PLACE_REMIX_API } from '~/routes/remix-api.location.find-place-by-keyword';
import type { loader } from '~/routes/remix-api.location.find-place-by-keyword';
import { useCountryContext } from '@archipro-website/localisation';

interface UsePlaceFinderProps {
    /** The maximum number of locations to return. Defaults to `10`. */
    limit?: number;
    /** The type of locations to return. Defaults to `FindPlaceTypeEnum.SuburbMixed`. */
    type?: FindPlaceTypeEnum;
    /** The country to search in. Defaults to the site country. */
    country?: SupportedCountry;
    /** The API provider to use. Defaults to `FindPlaceApiProviderEnum.Mapbox`. */
    apiProvider?: FindPlaceApiProviderEnum;
    /** The debounce time in milliseconds. Defaults to `DEFAULT_PLACE_FINDER_DEBOUNCE_MS`. */
    debounceMs?: number;
}

/**
 * Default debounce before firing a place search. Kept short so suggestions
 * feel instant; Remix fetchers cancel the in-flight request on resubmit, so
 * rapid typing does not stack requests.
 */
export const DEFAULT_PLACE_FINDER_DEBOUNCE_MS = 150;

/** Minimum keyword length before a place search request is sent. */
export const MIN_PLACE_FINDER_KEYWORD_LENGTH = 2;

// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1777139713/Place+Finder
// gql: packages/web/app/modules/location/graphql/query/find-place.graphql
// api: /remix-api/location/find-place-by-keyword

/**
 * Hook to search for a list of locations by keyword.
 * @todo Merge with `usePlaceFinder` in ui-rocco.
 */
export function usePlaceFinder({
    limit = 10,
    type = FindPlaceTypeEnum.SuburbMixed,
    country,
    apiProvider = FindPlaceApiProviderEnum.Mapbox,
    debounceMs = DEFAULT_PLACE_FINDER_DEBOUNCE_MS,
}: UsePlaceFinderProps) {
    const fetcher = useFetcher<typeof loader>();
    const [places, setPlaces] = useState<DetailedPlace[]>([]);
    /** Keyword that produced the current `places` list (trimmed). */
    const [optionsKeyword, setOptionsKeyword] = useState('');
    const { code: countryCode } = useCountryContext();
    // Tracks whether the latest keyword was searchable. When the user clears
    // the input we must ignore any late response from a previous request.
    const lastKeywordSearchable = useRef(false);
    // Last keyword sent to the API; paired with fetcher.data updates.
    const lastSubmittedKeyword = useRef('');
    // Bumps whenever fetcher.data changes; used to detect failed loads that
    // leave the previous fetcher.data in place.
    const fetchGenerationRef = useRef(0);
    const fetchGenerationAtSubmitRef = useRef<number | null>(null);
    const searchCountry = useMemo(
        () => country ?? countryCode,
        [country, countryCode],
    );
    const dropdownOptions = useMemo(
        () => getPlaceDropdownOptions(places),
        [places],
    );
    const searchFilterOptions = useMemo(
        () => getSearchFilterOptions(places),
        [places],
    );

    const submitKeyword = useCallback(
        (newKeyword?: string) => {
            const keyword = newKeyword?.trim() ?? '';
            if (keyword.length < MIN_PLACE_FINDER_KEYWORD_LENGTH) {
                lastKeywordSearchable.current = false;
                lastSubmittedKeyword.current = '';
                fetchGenerationAtSubmitRef.current = null;
                setOptionsKeyword('');
                setPlaces([]);
                return;
            }
            lastKeywordSearchable.current = true;
            lastSubmittedKeyword.current = keyword;
            fetchGenerationAtSubmitRef.current = fetchGenerationRef.current;
            try {
                const formData = new FormData();
                formData.append('keyword', keyword);
                formData.append('type', type.toString());
                formData.append('limit', limit.toString());
                formData.append('country', searchCountry.toString());
                formData.append('apiProvider', apiProvider.toString());
                // Remix fetchers cancel any in-flight request on resubmit, so
                // the latest keyword always wins.
                fetcher.submit(formData, {
                    method: 'get',
                    action: FIND_PLACE_REMIX_API,
                });
            } catch (error) {
                console.error(error);
                setPlaces([]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type, limit, searchCountry, apiProvider],
    );

    const { run: changeKeyword, flush: flushKeyword } = useDebounceFn(
        submitKeyword,
        { wait: debounceMs },
    );

    useEffect(() => {
        // Ignore late responses after the input was cleared.
        if (!lastKeywordSearchable.current) return;
        fetchGenerationRef.current += 1;
        setPlaces(fetcher?.data ?? []);
        setOptionsKeyword(lastSubmittedKeyword.current);
    }, [fetcher.data]);

    useEffect(() => {
        if (fetcher.state !== 'idle') return;
        if (fetchGenerationAtSubmitRef.current === null) return;

        const submittedAtGeneration = fetchGenerationAtSubmitRef.current;
        fetchGenerationAtSubmitRef.current = null;

        // Failed loader responses keep the previous fetcher.data, so clear
        // stale suggestions when no new data arrived for this submission.
        if (fetchGenerationRef.current <= submittedAtGeneration) {
            setPlaces([]);
            setOptionsKeyword('');
        }
    }, [fetcher.state]);

    return {
        /** The list of places/locations fetched from the API. */
        data: places,
        /** The loading state of the fetcher. */
        loading: fetcher.state !== 'idle',
        /** The function to update the search keyword. */
        changeKeyword,
        /**
         * Immediately fires any pending debounced search. Use when the user
         * signals intent (e.g. pressing Enter) so they never wait out the
         * debounce window.
         */
        flushKeyword,
        /**
         * The places/locations in the dropdown options format. Used for
         * address/postcode/location dropdowns.
         */
        dropdownOptions,
        /**
         * The places/locations in the search filter options format. Used for
         * search filter dropdowns.
         */
        searchFilterOptions,
        /**
         * Trimmed keyword that produced the current `searchFilterOptions`.
         * Empty when there are no results or the input was cleared.
         */
        optionsKeyword,
    };
}

/**
 * Format a location/place to a readable string for search filter dropdown options.
 * @param place The place/location to format.
 * @returns A readable string for search filter dropdown options.
 */
export function formatLocationSearchFilterText(place: {
    placeType?: DetailedPlace['placeType'];
    suburb?: string;
    district?: string;
    city?: string;
    division?: string;
    region?: string;
    state?: string;
    country?: string;
}) {
    let placeType = place.placeType;
    if (!placeType && !place.suburb && place.district) {
        placeType = 'district';
    }
    const segments = [
        place.suburb?.trim(),
        // Show only city or district, unless district type. A county
        // (division) shows only when the place names neither.
        ...(placeType === 'district'
            ? [place.district?.trim(), place.city?.trim()]
            : [
                  place.city?.trim() ||
                      place.district?.trim() ||
                      place.division?.trim(),
              ]),
        place.region?.trim(),
        // Do not show state for New Zealand (North Island and South Island).
        place.country !== 'New Zealand' ? place.state?.trim() : '',
    ].filter(Boolean);

    return [...new Set(segments)].join(', ');
}

/**
 * Generate dropdown options for search filters.
 * @param places The places/locations fetched from the API.
 * @returns A list of dropdown options for search filters.
 */
function getSearchFilterOptions(places: DetailedPlace[]) {
    return places.map((place) => ({
        header: formatLocationSearchFilterText(place),
        value: place,
        placeType: formatPlaceType(place.placeType, place.country),
    }));
}

/**
 * The type label shown under a place in the search filter dropdown options.
 * Exported for its spec.
 */
export function formatPlaceType(
    placeType: DetailedPlace['placeType'],
    country?: string,
) {
    switch (placeType) {
        case 'address':
            return 'Address';
        case 'postcode':
            return 'Postcode';
        case 'suburb':
            return 'Suburb';
        case 'district':
            return 'District';
        case 'city':
            return 'City';
        case 'division':
            // A division is a GeoNames admin2 area: a US county, or an equivalent
            // such as a Louisiana parish.
            return 'County';
        case 'region':
            // US admin1 areas come back as `region` but are labelled `State`.
            return getRegionNameByCountry(country) || 'Region';
        case 'state':
            return 'State';
        case 'country':
            return 'Country';
        default:
            return 'Other';
    }
}
